/**
 * Phase 14.5 Step 3 — `/degradation-profiles/:profileId` detail page.
 *
 * Replaces the Step 1 placeholder. Fetches the profile and the
 * registry on mount, then mounts the editor in `mode="edit"` (or
 * read-only mode when `is_seeded`).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Alert, AlertDescription } from "@/components/ui/alert";
import DegradationProfileEditor from "@/components/degradation/DegradationProfileEditor";
import degradationProfiles from "@/api/degradationProfiles";

export default function DegradationProfileDetailPage() {
    const { profileId } = useParams();
    const [profile, setProfile] = useState(null);
    const [registry, setRegistry] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            degradationProfiles.get(profileId),
            degradationProfiles.registry(),
        ])
            .then(([p, r]) => {
                if (cancelled) return;
                setProfile(p);
                setRegistry(r);
            })
            .catch((e) => {
                if (!cancelled) setErr(e?.response?.data?.detail || e?.message || "Failed to load profile.");
            });
        return () => { cancelled = true; };
    }, [profileId]);

    const isSeeded = !!profile?.is_seeded;

    return (
        <div className="space-y-4" data-testid="degradation-profile-detail-page">
            <PageHeader
                title={profile?.name || "Degradation profile"}
                subtitle={profile?.description || `Profile id: ${profileId || "—"}`}
            />
            <p
                className="text-xs text-ux-text-3"
                data-testid="degradation-profile-detail-id-readout"
            >
                {profileId || ""}
            </p>

            {err && (
                <Alert variant="destructive" data-testid="degradation-profile-detail-error">
                    <AlertDescription>{String(err)}</AlertDescription>
                </Alert>
            )}

            {(!profile || !registry) && !err && (
                <div
                    className="flex items-center gap-2 text-sm text-ux-text-3"
                    data-testid="degradation-profile-detail-loading"
                >
                    <Loader2 className="size-4 animate-spin" />
                    Loading…
                </div>
            )}

            {profile && registry && (
                <DegradationProfileEditor
                    mode="edit"
                    profileId={profileId}
                    initialValue={profile}
                    registry={registry}
                    readOnly={isSeeded}
                />
            )}
        </div>
    );
}
